import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import useGridInputs from '~/gridv1_1/hooks/useGridInputs';
import { getPSRC } from '~/gridv1_1/utils/activeProduct';
import PlusIcon from '~/icons/components/PlusIcon';
import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import { Link } from '~/techstyle-shared/next-routes';
import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import useId from '~/techstyle-shared/react-components/src/useId.tsx';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

import useNumGridCols from '../../hooks/useNumGridCols';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';

const SwatchWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 8px 0;
`;

const SwatchGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${({ theme }) => theme.gridCellStyles.gridSwatchInputGap}
`;

const SwatchCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: transparent;

  /* Inactive swatches: light gray border */
  border: ${({ isActive, theme }) =>
    isActive ? '2px solid transparent' : '2px solid rgb(237, 237, 237)'};

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  /* Inner white ring */
  &::before {
    top: 5px;
    left: 5px;
    right: 5px;
    bottom: 5px;
    background: ${({ isActive }) => (isActive ? 'white' : 'transparent')};
    z-index: 1;
  }

  /* Outer black ring */
  &::after {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    box-shadow: ${({ isActive, theme }) =>
      isActive
        ? `0 0 0 2px ${theme.colors.gridProductCardSwatchBorderSelected}`
        : 'none'};
    z-index: 2;
  }
  ${({ theme }) => theme.gridCellStyles.gridSwatchInputStyle}
`;

const SwatchImage = styled.img`
  width: ${({ isActive, theme }) =>
    isActive
      ? theme.gridCellStyles.gridSwatchImageActiveStyle
      : theme.gridCellStyles.gridSwatchImageDefaultStyle};
  height: ${({ isActive, theme }) =>
    isActive
      ? theme.gridCellStyles.gridSwatchImageActiveStyle
      : theme.gridCellStyles.gridSwatchImageDefaultStyle};
  border-radius: 50%;
  position: relative;
  z-index: 3;
  background-color: ${({ isActive, theme }) =>
    isActive
      ? theme.colors.gridProductCardSwatchBackgroundSelected
      : 'transparent'};
`;

const MoreSwatchesLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gridProductCardSwatchMoreSwatchesIcon};
  ${({ theme }) => theme.gridCellStyles.gridSwatchMoreIconStyle}
`;

const StyledSkeleton = styled(SkeletonLine)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme }) => theme.colors.gridProductCardSwatchSkeletonBorder};
`;

const getMaxSwatchesToRender = (
  numGridCols,
  fourColBreakpoint,
  twoColBreakpoint
) => {
  if (numGridCols >= 4) {
    return fourColBreakpoint ? 3 : 4;
  }

  // FL has 3 max grid columns
  return twoColBreakpoint ? 4 : 5;
};

const DEFAULT_FUNCTION = () => {};
const DEFAULT_SWATCHES = [];

const ProductCardSwatchesDesktop = ({
  swatches = DEFAULT_SWATCHES,
  activeProductId,
  activeProduct,
  setActiveProductId = DEFAULT_FUNCTION,
  trackProductSwatchClick = DEFAULT_FUNCTION,
}) => {
  const { productSource, gridType, isQuickViewEnabled } = useGridInputs();
  const quickViewContext = useQuickViewContext();
  const openQuickViewModal = quickViewContext?.openQuickViewModal;
  const { isRelatedProductsLoading, isRelatedProductsFetching } =
    useUnifiedProductGridData();
  const { matches: twoColBreakpoint } = useBreakpoint('(max-width: 835px)');
  const { matches: fourColBreakpoint } = useBreakpoint('(max-width: 1170px)');
  const numGridCols = useNumGridCols();
  const maxSwatchesToRender = getMaxSwatchesToRender(
    numGridCols,
    fourColBreakpoint,
    twoColBreakpoint
  );
  const elementId = useId();

  const isActive = (productId) => productId === activeProductId;

  // See ProductCardSwatches.md for details on this loading logic
  if (
    isRelatedProductsLoading ||
    (swatches?.length <= 1 && isRelatedProductsFetching)
  ) {
    return (
      <SwatchWrapper>
        {Array.from({ length: maxSwatchesToRender + 1 }, (_, index) => (
          <StyledSkeleton key={index} />
        ))}
      </SwatchWrapper>
    );
  }

  const handleQuickViewClick = ({ e, swatch }) => {
    if (swatch) {
      trackProductSwatchClick(swatch);
    }
    if (isQuickViewEnabled) {
      e.preventDefault();
      e.stopPropagation();

      openQuickViewModal(e, {
        product: activeProduct,
        psrc: getPSRC({ psrc: productSource, gridType }),
      });
    }
  };

  return (
    <SwatchWrapper data-testid="grid-product-swatches">
      <SwatchGroup>
        {swatches?.slice(0, maxSwatchesToRender).map((swatch) => (
          <Link key={swatch?.productId} href={swatch?.pdpLink}>
            <a
              onMouseEnter={() => {
                if (swatch?.productId !== activeProductId) {
                  setActiveProductId(swatch?.productId);
                }
              }}
              data-masterproductid={swatch?.productId}
              data-autotag={`${slugify(swatch?.label, '-')}-${elementId}`}
            >
              <SwatchCircle
                isActive={isActive(swatch?.productId)}
                onClick={(e) => handleQuickViewClick({ e, swatch })}
              >
                <SwatchImage
                  isActive={isActive(swatch?.productId)}
                  src={swatch?.url}
                  alt={swatch?.label}
                  title={swatch?.label}
                  onError={(e) => {
                    e.target.onerror = null; // prevents looping
                    e.target.style.display = 'none'; // hide the image
                  }}
                />
              </SwatchCircle>
            </a>
          </Link>
        ))}
      </SwatchGroup>

      {swatches.length > maxSwatchesToRender && (
        <Link href={activeProduct?.pdpLink}>
          <MoreSwatchesLink
            onClick={(e) => handleQuickViewClick({ e, swatch: null })}
          >
            <PlusIcon size={18} />
          </MoreSwatchesLink>
        </Link>
      )}
    </SwatchWrapper>
  );
};

ProductCardSwatchesDesktop.propTypes = {
  activeProduct: PropTypes.object,
  activeProductId: PropTypes.number,
  setActiveProductId: PropTypes.func,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  trackProductSwatchClick: PropTypes.func,
};

export default ProductCardSwatchesDesktop;
